<template>
  <div class="news">
    <div v-if="isBlocksLoading" class="state-text">加载中...</div>
    <div v-else-if="blockList.length === 0" class="state-text">暂无栏目数据</div>
    <el-tabs v-else v-model="activeName" @tab-click="handleTabClick" stretch>
      <el-tab-pane
        v-for="(block, index) in blockList"
        :key="getBlockKey(block, index)"
        :label="getBlockLabel(block, index)"
        :name="String(getBlockKey(block, index))"
      >
        <div v-if="isListLoading" class="state-text">加载中...</div>
        <div v-else-if="currentArticleList.length === 0" class="state-text">暂无文章数据</div>
        <div
          v-else
          v-for="(item, articleIndex) in currentArticleList"
          :key="getArticleKey(item, articleIndex)"
          class="news-content"
          @click="goDetail(item)"
        >
          <div class="news-title">{{ getArticleTitle(item) }}</div>
          <div class="news-time">{{ getArticleDate(item) }}</div>
          <img
            v-if="getArticleImage(item)"
            :src="getArticleImage(item)"
            class="news-image"
            alt="文章图片"
          >
        </div>
      </el-tab-pane>
    </el-tabs>
  </div>
</template>

<script>
import { getArticleBlocks, getArticleList } from '@/api';

const BLOCK_ID_KEYS = ['id', 'blockId', 'block_id', 'column_id', 'cate_id', 'category_id'];
const BLOCK_TITLE_KEYS = ['title', 'name', 'block_name', 'column_name', 'cate_name'];
const ARTICLE_ID_KEYS = ['id', 'article_id', 'content_id', 'record_id'];
const ARTICLE_TITLE_KEYS = ['title', 'article_title', 'name'];
const ARTICLE_DATE_KEYS = ['publishedAt', 'createdAt', 'publish_time', 'published_at', 'create_time', 'created_at', 'date'];
const ARTICLE_IMAGE_KEYS = ['coverUrl', 'cover', 'cover_url', 'image', 'img', 'thumb', 'pic'];

export default {
  name: 'HomeNews',
  data() {
    return {
      activeName: '',
      blockList: [],
      currentArticleList: [],
      isBlocksLoading: false,
      isListLoading: false,
    };
  },
  mounted() {
    this.fetchBlocks();
  },
  methods: {
    normalizeList(payload) {
      if (Array.isArray(payload)) {
        return payload;
      }
      if (Array.isArray(payload?.list)) {
        return payload.list;
      }
      if (Array.isArray(payload?.rows)) {
        return payload.rows;
      }
      if (Array.isArray(payload?.records)) {
        return payload.records;
      }
      return [];
    },
    getMatchedValue(item, keys, fallback = '') {
      for (let index = 0; index < keys.length; index += 1) {
        const value = item?.[keys[index]];
        if (value !== undefined && value !== null && value !== '') {
          return value;
        }
      }
      return fallback;
    },
    getBlockKey(block, index) {
      return this.getMatchedValue(block, BLOCK_ID_KEYS, `block-${index}`);
    },
    getBlockLabel(block, index) {
      return this.getMatchedValue(block, BLOCK_TITLE_KEYS, `栏目${index + 1}`);
    },
    getArticleKey(item, index) {
      return this.getMatchedValue(item, ARTICLE_ID_KEYS, `article-${index}`);
    },
    getArticleTitle(item) {
      return this.getMatchedValue(item, ARTICLE_TITLE_KEYS, '未命名文章');
    },
    getArticleDate(item) {
      return this.getMatchedValue(item, ARTICLE_DATE_KEYS, '--');
    },
    getArticleImage(item) {
      return this.getMatchedValue(item, ARTICLE_IMAGE_KEYS, '');
    },
    buildListParams(block) {
      const blockId = this.getMatchedValue(block, BLOCK_ID_KEYS, '');
      const params = {
        blockId,
        page_num: 1,
        page_size: 10,
      };
      return params;
    },
    async fetchBlocks() {
      this.isBlocksLoading = true;
      try {
        const res = await getArticleBlocks();
        const list = this.normalizeList(res?.data);
        this.blockList = list;
        if (list.length > 0) {
          this.activeName = String(this.getBlockKey(list[0], 0));
          this.fetchArticleListByActiveBlock();
        } else {
          this.currentArticleList = [];
        }
      } catch (error) {
        this.blockList = [];
        this.currentArticleList = [];
      } finally {
        this.isBlocksLoading = false;
      }
    },
    getBlockByName(blockName = this.activeName) {
      const targetName = String(blockName || '');
      return this.blockList.find((item, index) => String(this.getBlockKey(item, index)) === targetName) || null;
    },
    async fetchArticleListByActiveBlock(blockName = this.activeName) {
      const block = this.getBlockByName(blockName);
      if (!block) {
        this.currentArticleList = [];
        return;
      }

      this.isListLoading = true;
      try {
        const params = this.buildListParams(block);
        if (!params.blockId) {
          this.currentArticleList = [];
          return;
        }
        const res = await getArticleList(params);
        this.currentArticleList = this.normalizeList(res?.data);
      } catch (error) {
        this.currentArticleList = [];
      } finally {
        this.isListLoading = false;
      }
    },
    handleTabClick() {
      this.$nextTick(() => {
        this.fetchArticleListByActiveBlock(this.activeName);
      });
    },
    goDetail(item) {
      const articleId = this.getMatchedValue(item, ARTICLE_ID_KEYS, '');
      const query = articleId ? { articleId: String(articleId) } : {};
      this.$router.push({
        path: '/home/article/detail',
        query,
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.news {
    margin: 0 auto;
    width: 100%;

    ::v-deep .el-tabs__item {
        font-size: 4.61538vw;
        padding: 0 1.28205vw;
        flex: none !important;
    }

    ::v-deep .el-tabs__nav-wrap::after {
        background-color: transparent;
    }

    ::v-deep .el-tabs__nav-wrap {
        overflow-x: auto;
        overflow-y: hidden;
        -webkit-overflow-scrolling: touch;
        scrollbar-width: none;
    }

    ::v-deep .el-tabs__nav-wrap::-webkit-scrollbar {
        display: none;
    }

    /* 在PC端（支持鼠标的设备）显示很细的滚动条方便拖动 */
    @media (hover: hover) and (pointer: fine) {
        ::v-deep .el-tabs__nav-wrap {
            scrollbar-width: thin;
            scrollbar-color: #dcdfe6 transparent;
            padding-bottom: 4px;
        }
        ::v-deep .el-tabs__nav-wrap::-webkit-scrollbar {
            display: block;
            height: 4px;
        }
        ::v-deep .el-tabs__nav-wrap::-webkit-scrollbar-thumb {
            background-color: #dcdfe6;
            border-radius: 2px;
        }
        ::v-deep .el-tabs__nav-wrap::-webkit-scrollbar-track {
            background-color: transparent;
        }
    }

    ::v-deep .el-tabs__nav-scroll {
        overflow: visible !important;
    }

    ::v-deep .el-tabs__nav {
        display: flex;
        float: none;
        width: max-content;
        transform: none !important;
    }

    ::v-deep .el-tabs__nav-prev,
    ::v-deep .el-tabs__nav-next {
        display: none !important;
    }

    ::v-deep .el-tabs__nav-wrap.is-scrollable {
        padding: 0;
    }

    ::v-deep .el-tabs__active-bar {
        height: 0.76923vw;
        background-color: red;
    }

    ::v-deep .el-tabs__item.is-active {
        color: black;
    }

    ::v-deep .el-tabs__content {
        min-height: 0;
        margin-bottom: 0;
        overflow-y: auto;
    }

    .state-text {
        padding: 5vw 0;
        color: #999;
        font-size: 3.58974vw;
        text-align: center;
    }

    .news-content {
        position: relative;
        width: 100%;
        min-height: 33.33333vw;
        border-bottom: 0.25641vw dashed #dfdfdf;
        margin-bottom: 2.5641vw;
        cursor: pointer;
    }
    
    .news-title {
        position: absolute;
        top: 0;
        left: 0;
        width: 60%;
        font-size: 4.10256vw;
        font-weight: bold;
        text-align: left;
    }

    .news-time {
        position: absolute;
        bottom: 0;
        left: 0;
        font-size: 3.58974vw;
        color: #999999;
    }

    .news-image {
        position: absolute;
        top: 0;
        right: 0;
        width: 30.76923vw;
        height: 30.76923vw;
        border-radius: 3.84615vw;
        object-fit: cover;
    }
}
</style>